import type {
  CalendarEventAttendee,
  GranolaMcpMeetingEntity,
} from "@cohesive/api-entities";
import { MeetingCard } from "@cohesive/ui-components";
import { Notebook } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import { BaseSelectorProps } from "../BaseSelector";
import {
  Selector,
  SelectorEmpty,
  SelectorLoading,
  SelectorResults,
  SelectorSearch,
} from "../Selector";
import { useGranolaListMeetings } from "./useGranolaListMeetings";

function normalizeEntity(
  raw: Record<string, unknown>,
): GranolaMcpMeetingEntity | null {
  if (!raw) return null;

  const entity = raw as Partial<GranolaMcpMeetingEntity>;
  if (entity.type && entity.data) return entity as GranolaMcpMeetingEntity;

  if (raw.id && (raw.title || raw.start_time)) {
    return {
      type: "granola_mcp_meeting",
      base: "meeting",
      platform: "granola",
      data: {
        id: String(raw.id),
        title: String(raw.title ?? "Untitled Meeting"),
        start_time: raw.start_time as string | undefined,
        end_time: raw.end_time as string | undefined,
        web_link: raw.web_link as string | undefined,
        attendees: raw.attendees as CalendarEventAttendee[] | undefined,
        enhanced_notes: raw.enhanced_notes as string | undefined,
        private_notes: raw.private_notes as string | undefined,
        granola_url: raw.granola_url as string | undefined,
      },
    };
  }
  return null;
}

function matchesQuery(meeting: GranolaMcpMeetingEntity, q: string): boolean {
  const { data } = meeting;

  if (data.title?.toLowerCase().includes(q)) return true;

  if (data.attendees) {
    for (const a of data.attendees) {
      const attendee = a as CalendarEventAttendee;
      if (attendee.display_name?.toLowerCase().includes(q)) return true;
      if (attendee.email?.toLowerCase().includes(q)) return true;
    }
  }

  if (data.start_time) {
    try {
      const dateStr = new Date(data.start_time).toLocaleDateString();
      if (dateStr.toLowerCase().includes(q)) return true;
    } catch {
      // ignore parse errors
    }
  }

  return false;
}

export const GranolaMCPSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, isError } = useGranolaListMeetings({ connection });

  const allMeetings = useMemo(() => {
    const result: GranolaMcpMeetingEntity[] = [];
    for (const page of data?.pages ?? []) {
      const d = page.data;
      if (Array.isArray(d)) {
        for (const item of d) {
          if (
            item &&
            typeof item === "object" &&
            "type" in item &&
            "data" in item
          ) {
            result.push(item as GranolaMcpMeetingEntity);
          } else {
            const normalized = normalizeEntity(item as Record<string, unknown>);
            if (normalized) result.push(normalized);
          }
        }
      }
    }
    return result;
  }, [data]);

  const filteredMeetings = useMemo(() => {
    if (!searchQuery) return allMeetings;
    const q = searchQuery.toLowerCase();
    return allMeetings.filter((meeting) => matchesQuery(meeting, q));
  }, [allMeetings, searchQuery]);

  const meetingIds = useMemo(
    () => filteredMeetings.map((m) => m.data.id),
    [filteredMeetings],
  );

  // Generic projections (entity_format=both) keyed by entity id — placed on the
  // canvas in place of the typed meeting.
  const genericById = useMemo(
    () =>
      buildGenericById(
        data?.pages.flatMap((page) => toGenericArray(page.data_generic)) ?? [],
      ),
    [data],
  );

  return (
    <Selector>
      <SelectorSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search meetings..."
      />
      <SelectorResults ref={scrollRef}>
        {isLoading ? (
          <SelectorLoading />
        ) : !filteredMeetings.length ? (
          <SelectorEmpty
            icon={<Notebook className="h-12 w-12" />}
            message={
              isError
                ? "Failed to load meetings. Check that Granola is connected."
                : "No meetings found in the last 6 months"
            }
          />
        ) : (
          <>
            <div className="flex flex-col">
              {filteredMeetings.map((meeting) => (
                <EntityDragRow
                  key={meeting.data.id}
                  entity={meeting}
                  generic={genericById.get(meeting.data.id)}
                  entityId={meeting.data.id}
                  orderedIds={meetingIds}
                  onSelect={onSelect}
                  dragScope="granola-mcp"
                >
                  <MeetingCard
                    id={meeting.data.id}
                    platform={meeting.platform}
                    title={meeting.data.title || "Untitled Meeting"}
                    start_time={meeting.data.start_time}
                    end_time={meeting.data.end_time}
                    showJoinButton={false}
                    className="rounded-none shadow-none border-b hover:!bg-accent/50"
                  />
                </EntityDragRow>
              ))}
            </div>
            <div className="p-3 text-center border-t">
              <p className="text-xs text-muted-foreground">
                Showing meetings from the last 6 months
              </p>
            </div>
          </>
        )}
      </SelectorResults>
    </Selector>
  );
};
